import { notify } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { updateAttachment, uploadFile } from '@features/attachments/query-api'
import { useInvalidateTransactions } from '@features/transactions/hooks/useInvalidateTransactions'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../config/queryClient'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { TrackingContext } from '../../../enums/trackingContext'
import { useSegment } from '../../../hooks'
import { useInvalidateBills } from './useInvalidateBills'

interface UseUploadBillAttachmentsParams {
  billId?: string
  onUploadStart?: () => void
  onUploadFinished?: () => void
}

export const useUploadBillAttachments = ({
  billId,
  onUploadStart,
  onUploadFinished,
}: UseUploadBillAttachmentsParams) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { invalidateBills } = useInvalidateBills()
  const { invalidateTransactions } = useInvalidateTransactions()
  const [isUploading, setIsUploading] = useState(false)

  const handleFilesDropped = useCallback(
    async (filesAccepted: File[], _filesRejected?: FileRejection[]) => {
      if (!organization?.id || !billId) {
        return
      }

      setIsUploading(true)
      onUploadStart?.()

      try {
        for (const file of filesAccepted) {
          const response = await uploadFile(file, {
            'x-organizationId': organization.id,
            'x-should-scan': true,
            'x-create-attachment': true,
          })

          const attachmentId = response.attachments?.[0]?.id

          if (attachmentId) {
            await updateAttachment({
              id: attachmentId,
              ownerReference: `bill:${billId}`,
              ownerId: billId,
              organizationId: organization.id,
            })
          }
        }

        track('File Uploaded (Client)', { context: TrackingContext.BillUpload })
        queryClient.invalidateQueries([QueryKeys.Bill, billId])
        invalidateBills()
        invalidateTransactions()
        notify({
          id: NotificationKeys.BillAttachmentFilesUpdate,
          message: t('bill.notification.attachments_update_success'),
          variant: 'success',
        })
      } catch {
        notify({
          id: NotificationKeys.UploadFiles,
          message: t('file.rejected'),
          variant: 'error',
        })
      } finally {
        setIsUploading(false)
        onUploadFinished?.()
      }
    },
    [billId, organization, t, track, invalidateBills, invalidateTransactions, onUploadStart, onUploadFinished],
  )

  return { handleFilesDropped, isUploading }
}
